import { Button, Card, CardBody, CardFooter, CardHeader, CardText, CardTitle } from 'reactstrap';
import DealProjectionsLoader from './DealProjectionsLoader';
import CardInner from '../../../components/cards/CardInner';
import CardTitleGroup from '../../../components/cards/CardTitleGroup';
import CollapsibleCard from './ui/CollapsibleCard';
import DealRow from './ui/DealRow';
import { currencyFormatter } from '../../explorer/utils';
import { BlockTitle } from '../../../components/Component';
import CheckboxButton from '../../../components/input/checkbox/CheckboxButton';
import { useNavigate } from 'react-router-dom';

const DealProjectionsView = ({ data, isError, isLoading, refetch, includeEoi, setIncludeEoi }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return <DealProjectionsLoader />;
  }

  if (isError) {
    return <DealProjectionsError refetch={refetch} />;
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <>
        <BlockTitle>Projections</BlockTitle>
        <Card color="white" className="mt-4 rounded rounded">
          <CardHeader className='rounded rounded-lg'>
            <h5>No projections currently available.</h5>
          </CardHeader>
          <CardBody className='rounded rounded-lg'>
            <CardText className='fs-6'>
              <p>Projections are calculated based on the deals added to the system.</p>
              Please add deals to get projected earnings for the upcoming months & years.
            </CardText>
          </CardBody>
          <CardFooter className="d-flex justify-content-end bg-white" style={{ gap: '.5rem' }}>
            <Button color="primary" outline onClick={() => navigate('/list-deals')}>View Previous Deals</Button>
            <Button color="primary" onClick={() => navigate('/add-deal')}>Add Deal</Button>
          </CardFooter>
        </Card>
      </>
    );
  }

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  // const thisYearObj = data.find((y) => y.year === currentYear);
  // const thisMonthObj = thisYearObj?.months.find((m) => m.month === currentMonth);

  return (
    <>
      <div className="d-flex w-100 align-items-center justify-content-between pe-4 mt-4" id='projections-header'>
        <BlockTitle>Projections</BlockTitle>
        <CheckboxButton
          labelText="Include EOIs"
          id="includeEoi"
          name="includeEoi"
          labelPosition="left"
          labelClassName="text-muted fs-6 fw-bold"
          checked={includeEoi}
          onChange={() => {
            setIncludeEoi(!includeEoi);
            document.getElementById('projections-header').scrollIntoView({ behavior: 'smooth' });
          }}
        />
      </div>
      <div className="mt-4">
        {data.map((yearObj) => (
          <CollapsibleCard
            key={yearObj.year}
            className="bg-white p-2"
            headerClassName="bg-white shadow-none"
            cardBodyStyle={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}
            header={
              <div className="d-flex w-100 align-items-center justify-content-between">
                <h4 className="fw-bold">{yearObj.year === currentYear ? 'This Year' : yearObj.year} (Net)</h4>
                <span className="text-primary fs-6 fw-bold">
                  {currencyFormatter({ isDecimal: false }).format(yearObj.totalProjectedRevenue)}
                </span>
              </div>
            }
            defaultOpen={yearObj.year === currentYear}
          >
            {yearObj.months.map((monthObj) => (
              <CollapsibleCard
                key={monthObj.month}
                headerStyle={{ background: '#f6f6f6' }}
                cardBodyStyle={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}
                className="shadow-none"
                header={
                  <div className="d-flex w-100 align-items-center justify-content-between">
                    <h5>{new Date(0, monthObj.month - 1).toLocaleString('default', { month: 'long' })}</h5>
                    <span className="text-primary fs-6 fw-bold">
                      {currencyFormatter({ isDecimal: false }).format(monthObj.totalProjectedRevenue)}
                    </span>
                  </div>
                }
                defaultOpen={monthObj.month === currentMonth}
              >
                {monthObj.weeks.sort((a, b) => a.weekOfMonth - b.weekOfMonth).map((weekObj) => (
                  <CollapsibleCard
                    key={weekObj.weekOfMonth}
                    headerStyle={{ background: '#ececec', boxShadow: 'none' }}
                    style={{ border: 'none', boxShadow: 'none' }}
                    className="shadow-none"
                    header={
                      <div className="d-flex w-100 align-items-end justify-content-between">
                        <h6 className="mt-1">Week {weekObj.weekOfMonth}</h6>
                        <span className="text-primary fs-6 fw-bold">
                          {currencyFormatter({ isDecimal: false }).format(weekObj.totalProjectedRevenue)}
                        </span>
                      </div>
                    }
                  >
                    <div>
                      {weekObj.deals.map((deal, idx) => (
                        <DealRow
                          key={deal._id}
                          deal={deal}
                          className={`${idx !== weekObj.deals.length - 1 ? 'border-bottom py-2' : 'py-2'}`}
                        />
                      ))}
                    </div>
                  </CollapsibleCard>
                ))}
              </CollapsibleCard>
            ))}
          </CollapsibleCard>
        ))}
      </div>
    </>
  );
};

const DealProjectionsError = ({ refetch }) => {
  return (
    <Card className="w-100 mt-4 p-4">
      <CardInner className="d-flex flex-column align-items-center justify-content-center">
        <CardTitleGroup className="d-flex flex-column align-items-center justify-content-center">
          <h5 className="text-danger">Error while loading projections data</h5>
          <Button color="primary" outline onClick={refetch}>
            Retry
          </Button>
        </CardTitleGroup>
      </CardInner>
    </Card>
  );
};

export default DealProjectionsView;
